import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import FlowModuleListFilterBar from './FlowModuleListFilterBar';
import ListFlowEngineToolbar from './ListFlowEngineToolbar';
import FlowCaseFilterBar from './FlowCaseFilterBar';
import { flowListFilterSetExtra, flowListFilterSetTrayView } from '../../app/actions/flowListFilterActions';
import { startLoadingFlowEngineCatalogs } from '../../app/actions/flowEngineActions';
import { startLoadingCatalogCiiusByLevel1 } from '../../app/actions/catalogCiiuActions';
import { startLoadingCatalogsAll } from '../../app/actions/catalogActions';
import { listFlowRoleUsers } from '../../app/actions/userSearchActions';
import { FLOW_MODULE_DEFINITIONS } from '../../app/helpers/flowEngineConfigHelper';
import { CMI_SPECIALIST_USER_ROLE_KEYS } from '../../app/helpers/flowCaseMassHelper';
import { resolveImportStageFromState } from '../../app/helpers/importStageHelper';
import { listMyFlowCoverageGroups } from '../../app/actions/flowCoverageActions';
import { normalizeSpecialistRoleKey } from '../../app/helpers/activeSpecialistRoleHelper';
import { ALL_PHASE_FILTER_OPTIONS } from '../../app/helpers/flowCasePhaseHelper';
import {
  TRAY_TAB_MODE,
  TRAY_VIEW,
  resolveTrayTabMode
} from '../../app/helpers/flowTraySurfaceHelper';
import { fetchFlowCmiCasesRequest } from '../../app/actions/flowCmiActions';
import { resolveAuthUserId } from '../../app/helpers/flowEditPageHelper';
import useCanOperateFlowListRecord from '../../app/hooks/useCanOperateFlowListRecord';

const EMPTY_LIST_FILTERS = {
  text: '',
  from: '',
  to: '',
  catalogciius_id: '',
  catalogserie_id: '',
  state_catalog_id: '',
  current_user_id: '',
  flow_coverage_group_id: '',
  case_phase: ''
};

/**
 * Barra de filtros homologada + toolbar de flujo en listados de módulos (8).
 */
const FlowModuleListPanel = ({
  moduleProcess,
  moduleName,
  records = [],
  selectedIds = [],
  onSelectionChange,
  onAfterAction,
  totalAssigned = 0,
  showFlowListFilter = false,
  flowFilterActive = false,
  assignedOnly = true,
  showPending = false,
  showRejected = false,
  roleLabel = '',
  onAssignedOnlyChange,
  onShowPendingChange,
  onShowRejectedChange,
  showUserFilter = false
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const extra = useSelector((state) => state.flowListFilter?.extra || {});
  const trayView = useSelector((state) => state.flowListFilter?.trayView || TRAY_VIEW.MINE);
  const userRole = useSelector((state) => state.userRole);
  const isPreManager = resolveImportStageFromState({ userRole }) === 'pre';
  const { states: flowStates = [] } = useSelector((state) => state.flowEngine);
  const { listCatalogCiiusLevel1 = [] } = useSelector((state) => state.catalogCiiu);
  const { listSeries = [] } = useSelector((state) => state.catalog);
  const [draft, setDraft] = useState({ ...EMPTY_LIST_FILTERS, ...extra });
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [coverageGroups, setCoverageGroups] = useState([]);
  const [universeFacets, setUniverseFacets] = useState(null);
  const canOperateRecord = useCanOperateFlowListRecord();
  const authUser = useSelector((state) => state.auth?.user);
  const authUserId = resolveAuthUserId(authUser);
  const pageCount = records?.length ?? 0;
  const selected = userRole?.roleLogin?.isRoleSpecialistSelected
    ? userRole.roleLogin.roleSpecialistSelected
    : null;
  const roleKey = normalizeSpecialistRoleKey(selected);
  const tabMode = resolveTrayTabMode(roleKey);
  const showMineTab = tabMode === TRAY_TAB_MODE.THREE;
  const showPoolTab = tabMode === TRAY_TAB_MODE.THREE || tabMode === TRAY_TAB_MODE.POOL_GENERAL;
  const showTrayTabs = showMineTab || showPoolTab;
  const userFilterVisible = showUserFilter || (showTrayTabs && trayView === TRAY_VIEW.GENERAL);

  useEffect(() => {
    setDraft({ ...EMPTY_LIST_FILTERS, ...extra });
  }, [extra]);

  useEffect(() => {
    if (!showFlowListFilter) {
      return;
    }
    dispatch(startLoadingCatalogsAll());
    dispatch(startLoadingCatalogCiiusByLevel1(1));
    dispatch(startLoadingFlowEngineCatalogs());
  }, [dispatch, showFlowListFilter]);

  useEffect(() => {
    if (!showFlowListFilter) {
      return undefined;
    }
    const moduleId = FLOW_MODULE_DEFINITIONS[moduleProcess]?.moduleId;
    if (!moduleId || !roleKey) {
      setCoverageGroups([]);
      return undefined;
    }
    let cancelled = false;
    listMyFlowCoverageGroups({
      catalogmodule_id: moduleId,
      role: roleKey
    }).then((rows) => {
      if (!cancelled) {
        setCoverageGroups(rows);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [showFlowListFilter, moduleProcess, roleKey]);

  useEffect(() => {
    if (!showFlowListFilter || !userFilterVisible) {
      return undefined;
    }
    let cancelled = false;
    const moduleId = FLOW_MODULE_DEFINITIONS[moduleProcess]?.moduleId;
    listFlowRoleUsers(CMI_SPECIALIST_USER_ROLE_KEYS, moduleId).then((rows) => {
      if (!cancelled) {
        setUsers(rows);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [showFlowListFilter, userFilterVisible, moduleProcess]);

  useEffect(() => {
    if (!showFlowListFilter) {
      setUniverseFacets(null);
      return undefined;
    }
    let cancelled = false;
    const filters = {
      process: moduleProcess,
      pool_surface: 'list',
      active_catalogrole_id: selected?.id || '',
      active_role_key: roleKey || '',
      page: 0,
      size: 1
    };
    if (selected?.id && ['pre-manager', 'manager', 'validator', 'editor', 'coordinator'].includes(roleKey)) {
      filters.current_role_catalog_id = selected.id;
      filters.published_status = 'unpublished';
    }
    if (trayView === TRAY_VIEW.POOL) {
      filters.claimPool = true;
    } else if (trayView === TRAY_VIEW.GENERAL) {
      filters.assigned_scope = 'any';
    } else if (trayView === TRAY_VIEW.MINE && authUserId) {
      filters.current_user_id = authUserId;
    }
    fetchFlowCmiCasesRequest(filters).then((body) => {
      if (!cancelled && body?.ok) {
        setUniverseFacets(body.facets || null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [showFlowListFilter, moduleProcess, selected?.id, roleKey, trayView, authUserId]);

  const handleSearch = (event) => {
    event.preventDefault();
    dispatch(flowListFilterSetExtra(draft));
  };

  const handleClearFilters = () => {
    const next = { ...EMPTY_LIST_FILTERS };
    setDraft(next);
    dispatch(flowListFilterSetExtra(next));
  };

  const handleClearField = (field) => {
    const next = { ...draft, [field]: '' };
    setDraft(next);
    dispatch(flowListFilterSetExtra(next));
  };

  const selectTrayView = (nextView) => {
    if (trayView === nextView) {
      return;
    }
    dispatch(flowListFilterSetTrayView(nextView));
  };

  return (
    <>
      {showFlowListFilter && (
        <div className="px-3 px-md-4">
          <FlowCaseFilterBar
            filters={draft}
            onChange={setDraft}
            onSearch={handleSearch}
            modules={[]}
            states={flowStates}
            ciius={(listCatalogCiiusLevel1 || []).filter((item) => !item.parent_id)}
            series={listSeries}
            showModuleFilter={false}
            showTrayFilter={false}
            showUserFilter={userFilterVisible}
            userFirstRow={userFilterVisible}
            users={users}
            advancedOpen={advancedOpen}
            onToggleAdvanced={() => setAdvancedOpen((open) => !open)}
            showSeriesFilter={!isPreManager}
            showPhaseFilter
            phaseOptions={ALL_PHASE_FILTER_OPTIONS}
            phaseInAdvanced
            showClearFilters
            showFilterMarkers
            onClearFilters={handleClearFilters}
            onClearField={handleClearField}
            coverageGroups={coverageGroups}
            availableFacets={universeFacets}
          />
        </div>
      )}
      {showFlowListFilter && showTrayTabs && (
        <div className="px-3 px-md-4">
          <div className="btn-group mb-3" role="group" aria-label={t("specialist.selection.claim-pool.tabs", "Pestañas de la bandeja")}>
            {showMineTab && (
              <button
                type="button"
                className={`btn btn-sm ${trayView === TRAY_VIEW.MINE ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => selectTrayView(TRAY_VIEW.MINE)}
              >
                {t("specialist.selection.claim-pool.inbox", "Mi bandeja")}
              </button>
            )}
            {showPoolTab && (
              <button
                type="button"
                className={`btn btn-sm ${trayView === TRAY_VIEW.POOL ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => selectTrayView(TRAY_VIEW.POOL)}
              >
                {t("specialist.selection.claim-pool.pool", "Bandeja de Autoasignación")}
              </button>
            )}
            <button
              type="button"
              className={`btn btn-sm ${trayView === TRAY_VIEW.GENERAL ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => selectTrayView(TRAY_VIEW.GENERAL)}
            >
              {t("specialist.selection.claim-pool.general", "Bandeja General")}
            </button>
          </div>
          <p className="small text-muted mb-3">
            {trayView === TRAY_VIEW.POOL
              ? t(
                "specialist.selection.claim-pool.hint-pool",
                "Casos de ida o devolución sin usuario asignado. Reclama para pasarlos a Mi bandeja."
              )
              : trayView === TRAY_VIEW.GENERAL
                ? t(
                  "specialist.selection.claim-pool.hint-general",
                  "Todos los casos de esta bandeja que ya tienen responsable, incluidos los tuyos. El perfil de la bandeja consulta; el Coordinador opera aquí."
                )
                : t(
                  "specialist.selection.claim-pool.hint-mine",
                  "Solo los casos asignados a tu usuario en el rol de esta bandeja."
                )}
          </p>
        </div>
      )}
      <FlowModuleListFilterBar
        visible={showFlowListFilter}
        showRejected={showRejected}
        roleLabel={roleLabel}
        onShowRejectedChange={onShowRejectedChange}
      />
      <ListFlowEngineToolbar
        moduleProcess={moduleProcess}
        moduleName={moduleName || t(`${moduleProcess}.page.list`, moduleProcess)}
        records={records}
        selectedIds={selectedIds}
        onSelectionChange={onSelectionChange}
        onAfterAction={onAfterAction}
        pageCount={pageCount}
        totalAssigned={flowFilterActive ? totalAssigned : null}
        flowFilterActive={flowFilterActive}
        canSelectRecord={canOperateRecord}
      />
    </>
  );
};

FlowModuleListPanel.propTypes = {
  moduleProcess: PropTypes.string.isRequired,
  moduleName: PropTypes.string,
  records: PropTypes.array,
  selectedIds: PropTypes.array,
  onSelectionChange: PropTypes.func,
  onAfterAction: PropTypes.func,
  totalAssigned: PropTypes.number,
  showFlowListFilter: PropTypes.bool,
  flowFilterActive: PropTypes.bool,
  assignedOnly: PropTypes.bool,
  showPending: PropTypes.bool,
  showRejected: PropTypes.bool,
  roleLabel: PropTypes.string,
  onAssignedOnlyChange: PropTypes.func,
  onShowPendingChange: PropTypes.func,
  onShowRejectedChange: PropTypes.func,
  showUserFilter: PropTypes.bool
};

export default FlowModuleListPanel;
